import React from "react";
import { connect } from "react-redux";
import { Row, Col, InputGroup, Input, InputGroupText } from "reactstrap";
import DatePicker from "react-datepicker";
import datePickerS from "react-datepicker/dist/react-datepicker.css";
import withStyles from "isomorphic-style-loader/withStyles";
import classNames from "classnames";
import moment from "moment";
import ReactTooltip from "react-tooltip";
import { firebaseConnect, isLoaded, withFirebase } from "react-redux-firebase";
import get from "lodash.get";
import isEqual from "lodash.isequal";

import {
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  subscribeToResizeChanges,
  withLoginRedirect,
  withModals,
} from "../../composition";

import * as commonActions from "../../../utils/actions/common";
import { getMessages, getSendingPhoneNumber } from "../../../utils/fetch";
import { IChat } from "../../../types/IMessage";
import Chat from "../chat";
import ChatList from "../chatList";
import { formatPhoneNumber, phonePlainFormat } from "../../../utils/helpers";
import {
  isCurrentUserAdmin,
  isCurrentUserOnlyReader,
  isCurrentUserPublisher,
} from "../../../utils/roleCheck";

import s from "./styles.scss";
import firebase from "firebase/compat/app";
import { ExportType } from "../exportModal";
import { countUnreadThreads } from "../../../utils/messengerInbox";

interface Props extends ServiceCallerProps, ModalProps {
  mobileMode: boolean;
  firebase: firebase.app.App;
  hideSelection: () => void;
  showMessenger: boolean;
  disableMessenger: boolean;
  unreadMessages: { [key: string]: number };
  userId: string;
}

interface State {
  selectedDonorId: string;
  selectedChat: IChat;
  errorMessage: string;
  isLoadingChat: boolean;

  searchText: string;
  searchTextUsed: string;
  newMessageText: string;

  fromDate: Date;
  toDate: Date;
  calendarMode: boolean;

  sendingPhoneNumber?: string;
  inboxBusy: boolean;
}

class MessageCenterPage extends React.PureComponent<Props, State> {
  private messengerMainRef = React.createRef<HTMLElement>();
  private heldUnreadDonorId = "";

  state = {
    selectedDonorId: "",
    selectedChat: null,
    errorMessage: null,
    isLoadingChat: false,

    searchText: "",
    searchTextUsed: "",
    newMessageText: "",

    fromDate: null,
    toDate: null,
    calendarMode: false,

    sendingPhoneNumber: "",
    inboxBusy: false,
  };

  searchTimeout = null;

  showDonorDetail = (id: string) => {
    this.props.navigate(`/contacts/${id}`);
  };

  loadSendingPhoneNumber = (): void => {
    const { config, currentCharity, serviceAccessToken } = this.props;
    if (!config?.adminServiceUrl || !currentCharity?.id || !serviceAccessToken) {
      return;
    }
    getSendingPhoneNumber(
      config.adminServiceUrl,
      currentCharity.id,
      serviceAccessToken
    ).then(
      response => {
        const raw = response.phoneNumber || "";
        this.setState({
          sendingPhoneNumber: formatPhoneNumber(raw.toString()),
        });
      }
    );
  };

  componentDidMount() {
    this.props.hideSelection();

    if (
      !this.props.showMessenger ||
      (!isCurrentUserAdmin(this.props.currentCharity) &&
        !isCurrentUserPublisher(this.props.currentCharity) &&
        !isCurrentUserOnlyReader(this.props.currentCharity))
    ) {
      this.props.navigate("/contacts");
    }

    this.initializeDateRangeFromUrl();

    this.loadSendingPhoneNumber();
  }

  componentWillUnmount(): void {
    if (this.searchTimeout && window) {
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { selectedDonorId } = this.state;
    const {
      unreadMessages,
      showMessenger,
      currentCharity,
      disableMessenger,
    } = this.props;

    if (
      !showMessenger ||
      disableMessenger ||
      (!isCurrentUserAdmin(currentCharity) &&
        !isCurrentUserPublisher(currentCharity) &&
        !isCurrentUserOnlyReader(currentCharity))
    ) {
      this.props.navigate("/contacts");
    }

    if (unreadMessages && !isEqual(unreadMessages, prevProps.unreadMessages)) {
      if (
        unreadMessages[selectedDonorId] &&
        this.heldUnreadDonorId !== selectedDonorId
      ) {
        this.loadMessages(selectedDonorId, true);
      }
    }

    if (this.props.serviceAccessToken && !prevProps.serviceAccessToken) {
      this.loadSendingPhoneNumber();
    }

    const adminUrl = this.props.config?.adminServiceUrl;
    const prevAdminUrl = prevProps.config?.adminServiceUrl;
    const charityId = this.props.currentCharity?.id;
    const prevCharityId = prevProps.currentCharity?.id;

    if (
      adminUrl &&
      charityId &&
      (adminUrl !== prevAdminUrl || charityId !== prevCharityId)
    ) {
      if (charityId !== prevCharityId) {
        if (this.searchTimeout && window) {
          window.clearTimeout(this.searchTimeout);
          this.searchTimeout = null;
        }
        this.setState({
          selectedDonorId: "",
          selectedChat: null,
          isLoadingChat: false,
          searchText: "",
          searchTextUsed: "",
          fromDate: null,
          toDate: null,
          calendarMode: false,
          errorMessage: null,
          sendingPhoneNumber: "",
          inboxBusy: false,
        });
        this.heldUnreadDonorId = "";
        this.syncDateRangeToUrl(null, null);
      }
      this.loadSendingPhoneNumber();
    }

    if (
      this.props.config?.notificationServiceUrl &&
      !prevProps.config?.notificationServiceUrl &&
      this.state.selectedDonorId
    ) {
      this.loadMessages(this.state.selectedDonorId, true);
    }

    const prevDonorId = prevState.selectedChat?.donor?.id ?? "";
    const donorId = this.state.selectedChat?.donor?.id ?? "";
    if (donorId && donorId !== prevDonorId) {
      this.scrollMessengerIntoView();
    }
  }

  scrollMessengerIntoView = (): void => {
    if (typeof window === "undefined") {
      return;
    }
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.messengerMainRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  };

  initializeDateRangeFromUrl = (): void => {
    if (!this.props.location || !this.props.location.search) return;

    const params = new URLSearchParams(this.props.location.search);
    const from = params.get("from");
    const to = params.get("to");
    if (!from && !to) return;

    const parseDateParam = (value: string | null): Date | null => {
      if (!value) return null;
      // New format: YYYY-MM-DD (interpreted in user's local timezone).
      const m = moment(value, "YYYY-MM-DD", true);
      return m.isValid() ? m.toDate() : null;
    };

    this.setState({
      calendarMode: true,
      fromDate: parseDateParam(from),
      toDate: parseDateParam(to),
    });
  };

  syncDateRangeToUrl = (fromDate: Date, toDate: Date): void => {
    if (!this.props.location) return;

    const params = new URLSearchParams(this.props.location.search || "");

    if (fromDate) {
      params.set("from", moment(fromDate).format("YYYY-MM-DD"));
    } else {
      params.delete("from");
    }

    if (toDate) {
      params.set("to", moment(toDate).format("YYYY-MM-DD"));
    } else {
      params.delete("to");
    }

    const search = params.toString();
    this.props.navigate(
      {
        pathname: this.props.location.pathname,
        search: search ? `?${search}` : "",
      },
      { replace: true }
    );
  };

  fromDateChange = (fromDate: Date | null): void => {
    this.setState({
      fromDate,
      selectedDonorId: "",
      selectedChat: null,
    });
    this.syncDateRangeToUrl(fromDate, this.state.toDate);
  };

  toDateChange = (toDate: Date | null): void => {
    this.setState({
      toDate,
      selectedDonorId: "",
      selectedChat: null,
    });
    this.syncDateRangeToUrl(this.state.fromDate, toDate);
  };

  searchTextChange = (e): void => {
    const newText: string = phonePlainFormat(e.target.value);

    if (!newText || newText.length >= 3 || newText.length == 0) {
      if (this.searchTimeout && window) {
        window.clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.setState({
        searchText: newText,
        searchTextUsed: newText,
      });
    } else {
      if (window) {
        if (this.searchTimeout) {
          window.clearTimeout(this.searchTimeout);
        }
        this.setState({
          searchText: newText,
        });
        this.searchTimeout = window.setTimeout(() => {
          this.setState({
            searchTextUsed: newText,
          });
        }, 1500);
      }
    }
  };

  cleanCurrentChat = (): void => {
    this.heldUnreadDonorId = "";
    this.setState({
      selectedDonorId: "",
      selectedChat: null,
      isLoadingChat: false,
    });
  };

  cleanSearchField = (): void => {
    this.setState({
      searchText: "",
      searchTextUsed: "",
      fromDate: null,
      toDate: null,
      selectedDonorId: "",
      selectedChat: null,
      isLoadingChat: false,
    });
    this.syncDateRangeToUrl(null, null);
  };

  caledarButtonPressed = (): void => {
    this.cleanSearchField();
    this.setState({ calendarMode: !this.state.calendarMode });
  };

  exportConversations = (): void => {
    this.props.toggleExportModal(ExportType.conversations);
  };

  showSelectedChat = (donorId: string): void => {
    this.heldUnreadDonorId = "";
    this.setState({
      selectedDonorId: donorId,
    });
    this.loadMessages(donorId, false, true);
  };

  inboxRef = () => {
    const { firebase: firebaseApp, currentCharity, userId } = this.props;
    if (
      !firebaseApp ||
      typeof firebaseApp.database !== "function" ||
      !currentCharity ||
      !currentCharity.id ||
      !userId
    ) {
      return null;
    }
    return firebaseApp
      .database()
      .ref(currentCharity.id)
      .child("smsNotifications")
      .child(userId);
  };

  surfaceInboxWriteError = (): void => {
    alert("Could not update unread messages. Please try again.");
  };

  refreshUnreadMessages = (donorId: string, forceClear?: boolean) => {
    if (!forceClear && this.heldUnreadDonorId === donorId) {
      return;
    }
    const ref = this.inboxRef();
    if (!ref) {
      this.surfaceInboxWriteError();
      return;
    }
    ref.child(donorId).set(null).catch(() => {
      this.surfaceInboxWriteError();
    });
  };

  markAllAsReadKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.markAllAsRead();
    }
  };

  markAllAsRead = (): void => {
    if (this.state.inboxBusy || countUnreadThreads(this.props.unreadMessages) === 0) {
      return;
    }
    const ref = this.inboxRef();
    if (!ref) {
      this.surfaceInboxWriteError();
      return;
    }
    this.heldUnreadDonorId = "";
    this.setState({ inboxBusy: true });
    ref
      .remove()
      .then(() => {
        this.setState({ inboxBusy: false });
      })
      .catch(() => {
        this.setState({ inboxBusy: false });
        this.surfaceInboxWriteError();
      });
  };

  markSelectedUnread = (): void => {
    const donorId = this.state.selectedDonorId;
    if (!donorId || this.state.inboxBusy) {
      return;
    }
    const current = this.props.unreadMessages && this.props.unreadMessages[donorId];
    if (typeof current === "number" ? current > 0 : Number(current) > 0) {
      return;
    }
    const ref = this.inboxRef();
    if (!ref) {
      this.surfaceInboxWriteError();
      return;
    }
    this.heldUnreadDonorId = donorId;
    this.setState({ inboxBusy: true });
    ref
      .child(donorId)
      .set(1)
      .then(() => {
        this.setState(
          {
            inboxBusy: false,
            selectedDonorId: "",
            selectedChat: null,
            isLoadingChat: false,
          },
          () => {
            this.heldUnreadDonorId = "";
          }
        );
      })
      .catch(() => {
        this.heldUnreadDonorId = "";
        this.setState({ inboxBusy: false });
        this.surfaceInboxWriteError();
      });
  };

  loadMessages = (
    donorID: string,
    silentRefresh?: boolean,
    forceClearUnread?: boolean
  ): void => {
    if (!donorID) {
      this.setState({
        selectedDonorId: "",
        selectedChat: null,
        isLoadingChat: false,
      });
      return;
    }
    const { config, currentCharity, serviceAccessToken } = this.props;
    if (!config?.notificationServiceUrl || !currentCharity?.id) {
      if (!silentRefresh) {
        this.setState({ isLoadingChat: false });
      }
      return;
    }
    if (!silentRefresh) {
      this.setState({
        isLoadingChat: true,
        selectedChat: null,
      });
    }
    getMessages(
      config.notificationServiceUrl,
      currentCharity.id,
      donorID,
      serviceAccessToken
    )
      .then(response => {
        if (this.state.selectedDonorId !== donorID) {
          return;
        }
        this.setState({
          selectedChat: response,
          selectedDonorId: response.donor.id,
          isLoadingChat: false,
        });
        this.refreshUnreadMessages(response.donor.id, forceClearUnread);
      })
      .catch(_ => {
        alert("There has been an error, please reload the site.");
        this.setState({
          selectedDonorId: "",
          selectedChat: null,
          isLoadingChat: false,
          errorMessage: "There has been an error, please reload the site.",
        });
      });
  };

  renderFilters = (): JSX.Element => {
    const { mobileMode } = this.props;
    return (
      <Row className={classNames(s.bottomBorder, s.filtersRow)}>
        <Col
          xs={12}
          md
          className={classNames(
            s.colPadding,
            s.colFlex,
            s.filtersCol,
            s.filtersMainCol
          )}
        >
          {
            <div className={s.calendarDiv}>
              <i
                data-tip
                data-for={`tpexport`}
                className={classNames("fa fa-file", s.calendarIcon)}
                onClick={this.exportConversations}
              />
              <ReactTooltip
                id={`tpexport`}
                type="info"
                place="bottom"
                effect="float"
              >
                {"Export All Conversations"}
              </ReactTooltip>
            </div>
          }
          {
            <div className={s.calendarDiv}>
              <i
                className={classNames(
                  "fa fa-calendar",
                  s.calendarIcon,
                  this.state.calendarMode && s.calendarIconSelected
                )}
                onClick={this.caledarButtonPressed}
              />
            </div>
          }
          <div className={s.calendarDiv}>
            <i
              data-tip
              data-for="tp-mark-all-read"
              className={classNames(
                "fa fa-envelope-open",
                s.calendarIcon,
                (this.state.inboxBusy ||
                  countUnreadThreads(this.props.unreadMessages) === 0) &&
                  s.calendarIconDisabled
              )}
              onClick={this.markAllAsRead}
              role="button"
              tabIndex={0}
              aria-label="Mark all as read"
              onKeyDown={this.markAllAsReadKeyDown}
            />
            <ReactTooltip
              id="tp-mark-all-read"
              type="info"
              place="bottom"
              effect="float"
            >
              Mark all as read
            </ReactTooltip>
          </div>
          <InputGroup className={s.inputDiv}>
            <InputGroupText className={s.inputGroup}>
              <i className={"fa fa-search " + s.searchIcon}>&nbsp;</i>
            </InputGroupText>

            {this.state.calendarMode ? (
              <div className={s.dateRange}>
                <DatePicker
                  id="datePickerFrom"
                  selected={this.state.fromDate}
                  onChange={this.fromDateChange}
                  className={classNames(
                    s.calendarText,
                    mobileMode && s.calendarTextMobile
                  )}
                  placeholderText={"From"}
                  maxDate={this.state.toDate || moment(new Date())}
                  autoComplete="off"
                  isClearable
                />
                <DatePicker
                  id="datePickerTo"
                  selected={this.state.toDate}
                  onChange={this.toDateChange}
                  className={classNames(
                    s.calendarText,
                    mobileMode && s.calendarTextMobile
                  )}
                  placeholderText={"To"}
                  minDate={this.state.fromDate}
                  maxDate={moment(new Date())}
                  autoComplete="off"
                  isClearable
                />
              </div>
            ) : (
              <Input
                id="searchText"
                placeholder={"Search by phone number"}
                value={this.state.searchText}
                onChange={this.searchTextChange}
                className={classNames(s.text, mobileMode && s.textMobile)}
              />
            )}

            <InputGroupText
              className={classNames(s.inputGroupRight)}
            >
              <i
                className={"fa fa-times " + s.closeIcon}
                onClick={this.cleanSearchField}
              >
                &nbsp;
              </i>
            </InputGroupText>
          </InputGroup>
        </Col>
        {this.state.sendingPhoneNumber && (
          <Col
            xs={12}
            md="auto"
            className={classNames(s.colPadding, s.sendingNumberCol)}
          >
            <InputGroup className={s.sendingNumberContainer}>
              <InputGroupText className={s.inputGroupSendingNumber}>
                <i className={"fa fa-phone " + s.phoneIcon}>&nbsp;</i>{" "}
                Sending Number
              </InputGroupText>
              <Input
                id="sendingPhoneNumber"
                placeholder={this.state.sendingPhoneNumber}
                value={this.state.sendingPhoneNumber}
                className={s.phoneNumberText}
                readOnly
              />
            </InputGroup>
          </Col>
        )}
      </Row>
    );
  };

  render() {
    const { mobileMode } = this.props;

    const {
      selectedDonorId,
      selectedChat,
      isLoadingChat,
      searchTextUsed,
      fromDate,
      toDate,
    } = this.state;

    return (
      <main ref={this.messengerMainRef} className={s.main}>
        <div className={s.container}>
          {this.renderFilters()}

          <Row className="g-0">
            {(!mobileMode || (!selectedChat && !isLoadingChat)) && (
              <ChatList
                key={this.props.currentCharity?.id ?? "no-charity"}
                loadMessages={this.showSelectedChat}
                selectedDonorId={selectedDonorId}
                searchText={searchTextUsed}
                fromDate={fromDate}
                toDate={toDate}
                unreadMessages={this.props.unreadMessages}
              />
            )}

            {(!mobileMode || selectedChat || isLoadingChat) && (
              <Chat
                chat={selectedChat}
                chatLoading={isLoadingChat}
                back={this.cleanCurrentChat}
                onCloseChat={this.cleanCurrentChat}
                showDonorDetail={this.showDonorDetail}
                threadUnread={
                  !!(
                    selectedDonorId &&
                    this.props.unreadMessages &&
                    this.props.unreadMessages[selectedDonorId] > 0
                  )
                }
                inboxBusy={this.state.inboxBusy}
                onMarkUnread={this.markSelectedUnread}
              />
            )}
          </Row>
        </div>
      </main>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
});

const mapStateToProps = state => {
  const { common, firebase, user } = state;

  const unreadMessages =
    isLoaded(firebase.data[user.currentCharity.id]) &&
    isLoaded(firebase.data[user.currentCharity.id].smsNotifications)
      ? get(
          firebase,
          `data.${user.currentCharity.id}.smsNotifications[${user.id}]`
        )
      : {};

  const disableMessenger =
    isLoaded(firebase.data[user.currentCharity.id]) &&
    isLoaded(firebase.data[user.currentCharity.id].config) &&
    isLoaded(firebase.data[user.currentCharity.id].config.disableMessenger)
      ? get(firebase, `data.${user.currentCharity.id}.config.disableMessenger`)
      : false;

  return {
    userId: user.id,
    showMessenger: common.showMessenger,
    unreadMessages,
    disableMessenger:
      typeof disableMessenger !== "undefined" && disableMessenger !== null
        ? disableMessenger
        : false,
  };
};

export default withFirebase(
  serviceCaller(
    withLoginRedirect(
      connect(
        mapStateToProps,
        mapDispatchToProps
      )(
        firebaseConnect((_, store) => {
          return store.getState().user.currentCharity.id !== ""
            ? [
                `${store.getState().user.currentCharity.id}/smsNotifications/${
                  store.getState().user.id
                }`,
              ]
            : [];
        })(
          withModals(
            withStyles(
              s,
              datePickerS
            )(subscribeToResizeChanges(MessageCenterPage))
          )
        )
      )
    )
  )
);
